import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Form,
} from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { projectService } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import Header from "../layout/Header";

// Validation schema using Yup
const ProjectSchema = Yup.object().shape({
  title: Yup.string().required("Le titre est requis"),
  description: Yup.string().required("La description est requise"),
  author: Yup.string().required("L'auteur est requis"),
  year: Yup.number()
    .typeError("L'année doit être un nombre")
    .required("L'année est requise"),
  link: Yup.string().url("Lien invalide"),
});

const ProjetForm = () => {
  const { currentUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    title: "",
    description: "",
    author: "",
    year: "",
    link: "",
  });
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load existing project data if in edit mode
  useEffect(() => {
    if (id) {
      const fetchProject = async () => {
        try {
          const response = await projectService.getProject(id);
          setInitialValues({
            title: response.data.title,
            description: response.data.description,
            author: response.data.author,
            year: response.data.year,
            link: response.data.content.link || "",
          });
        } catch (err) {
          setError("Impossible de charger le projet.");
        } finally {
          setLoading(false);
        }
      };
      fetchProject();
    }
  }, [id]);

  // Form submission handler
  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setSuccess(false);

    const data = {
      title: values.title,
      description: values.description,
      author: values.author,
      year: values.year,
      content: { link: values.link },
      // created_by will be handled on the backend
    };
    console.log("Submitting data:", data);
    try {
      if (id) {
        // Update existing project
        const res = await projectService.updateProject(id, data);
        if (res.status === 200) {
          navigate(`/projects/${id}`);
        }
        setSuccess(true);
      } else {
        // Create new project
        const res = await projectService.createProject(data);
        if (res.status === 201 || res.status === 200) {
          const new_id = res.data.id;
          navigate(`/projects/${new_id}`);
        }
        setSuccess(true);
      }
    } catch (err) {
      setError(
        "Erreur lors de l'enregistrement. Veuillez vérifier les champs."
      );
    }
    setSubmitting(false);
  };

  // Display loading spinner while fetching data
  if (loading) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <div>
      <header className="navbar">
    
        {/* <img src="/logo.png" alt="Logo" className="logo" />
        <nav>
          <ul>
            <li>
              <a href="/">ACCUEIL</a>
            </li>
            <li>
              <a href="/projects/new">CONTRIBUER</a>
            </li>
            <li>
              <a href="/login">Se Connecter</a>
            </li>
            <li>
              <a href="/dictionary">TERMES</a>
            </li>
            <li>
              <a href="/projects">PROJETS</a>
            </li>
          </ul>
        </nav> */}
      </header>
      <Container
        className="my-1 d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <Card style={{ width: "50vw", maxWidth: 700 }}>
          <Card.Header as="h5" className="bg-primary text-white">
            {id ? "Modifier" : "Ajouter"} un projet
          </Card.Header>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
              <Alert variant="success">
                Projet enregistré ! En attente de validation par un
                administrateur.
              </Alert>
            )}

            <Formik
              initialValues={initialValues}
              validationSchema={ProjectSchema}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {({
                handleSubmit,
                handleChange,
                values,
                touched,
                errors,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="title">
                    <Form.Label>Titre</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      isInvalid={touched.title && !!errors.title}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.title}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      isInvalid={touched.description && !!errors.description}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.description}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="author">
                    <Form.Label>Auteur</Form.Label>
                    <Form.Control
                      type="text"
                      name="author"
                      value={values.author}
                      onChange={handleChange}
                      isInvalid={touched.author && !!errors.author}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.author}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="year">
                    <Form.Label>Année</Form.Label>
                    <Form.Control
                      type="number"
                      name="year"
                      value={values.year}
                      onChange={handleChange}
                      isInvalid={touched.year && !!errors.year}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.year}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="link">
                    <Form.Label>Lien du code (du projet complet)</Form.Label>
                    <Form.Control
                      type="url"
                      name="link"
                      value={values.link}
                      onChange={handleChange}
                      isInvalid={touched.link && !!errors.link}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.link}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Enregistrement...
                        </>
                      ) : id ? (
                        "Modifier"
                      ) : (
                        "Ajouter"
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ProjetForm;
